import { useEffect, useRef } from "react";
import type { ProcessNodeProps } from "../SComponents/ProcessNode";
import { mainRender } from "../engines/renderingEngine/renderer";
import { setBackgroundTheme } from "../pixi-components/layers/background/background.utils";
import { cleanup } from "../engines/renderingEngine/unrenderer";
import * as PIXI from "pixi.js";
import { initInteractionObserver } from "../engines/interactionEngine/interactionObserver";
import {
  initCursorChangeObserver,
  removeCursorChangeObserver
} from "../engines/interactionEngine/cursorChangeObserver";
import { renderFlags } from "../engines/layoutEngine/renderFlags";
import {
  initLayoutObserver,
  removeLayoutObserver
} from "../engines/layoutEngine/layoutObserver";
import { getViewport } from "../pixi-components/layers/viewport/viewport.layer";
import { usePrevious } from "@certa/common/hooks.ts";
import { devLogger } from "@certa/common/utils/devLogger.ts";
import type { FSC } from "../engines/renderingEngine/fsc.types";
import { lineRender } from "../engines/renderingEngine/lineRenderer";
import { FlowLineNode } from "../SComponents/FlowLineNode";
import type { ObjectNodeProps } from "../SComponents/ObjectNode";
import type { THEME } from "../SComponents/colorPalette.constants";
import {
  isRootNodeBoundsChanged,
  updateWorldBoundaries
} from "../engines/interactionEngine/worldBoundary";

export const useRendererInitializer = <
  T extends {
    theme: THEME;
    collapsed: string[];
    selectedKey: string;
  }
>({
  fsComponent: FSComponent,
  props,
  data
}: {
  fsComponent: FSC<T>;
  props: T;
  data: ProcessNodeProps["process"] | ObjectNodeProps["object"];
}) => {
  const propsRef = useRef(props);
  propsRef.current = props;

  const nodeRef = useRef<FSC<T>>();

  useEffect(() => {
    function domChangeRender() {
      // trigger a rerender if the domDirty flag is set
      // but make sure that a render is not already in progress
      if (renderFlags.domDirty) {
        mainRender(FSComponent, propsRef.current);
        lineRender(FlowLineNode, propsRef.current);
        if (isRootNodeBoundsChanged()) {
          // To keep the user inside a restricted world boundary, instead of panning infinitesimally.
          updateWorldBoundaries();
        }
        renderFlags.domDirty = false;
      }
    }
    if (nodeRef.current !== FSComponent) {
      // Clear and redraw the canvas when NodeComponent changes
      cleanup();
      mainRender(FSComponent, propsRef.current);
      if (isRootNodeBoundsChanged()) {
        // To keep the user inside a restricted world boundary, instead of panning infinitesimally.
        updateWorldBoundaries();
      }
      nodeRef.current = FSComponent;
    }
    // Previously was using requestIdleCallback but Safari doesn't support it.
    // Since Pixi already offers a low-priority queue, we can use that instead.
    // Alternatively we could use a setTimeout with 0ms delay, but this works
    // well.
    PIXI.Ticker.shared.add(domChangeRender, null, PIXI.UPDATE_PRIORITY.LOW);
    return () => {
      PIXI.Ticker.shared.remove(domChangeRender);
    };
  }, [FSComponent, props]);

  useEffect(() => {
    if (data) {
      mainRender(FSComponent, props);
      lineRender(FlowLineNode, props);

      if (isRootNodeBoundsChanged()) {
        // To keep the user inside a restricted world boundary, instead of panning infinitesimally.
        updateWorldBoundaries();
      }
      if (renderFlags.domDirty) {
        renderFlags.domDirty = false;
      }
    }
  }, [props, data, FSComponent]);

  useEffect(() => {
    return () => {
      devLogger.log("Cleanup");

      // Initiating cleanup
      cleanup();
    };
  }, []);
  const { theme, collapsed } = props;

  useEffect(() => {
    if (collapsed) {
      // Refer to TODO on layoutObserver
      renderFlags.linesDirty = true;
    }
  }, [collapsed]);

  useEffect(() => {
    setBackgroundTheme(theme);
    renderFlags.layoutDirty = true;
  }, [theme]);

  const { selectedKey } = props;
  const lastSelectedKey = usePrevious(selectedKey);
  useEffect(() => {
    if (selectedKey !== lastSelectedKey) {
      renderFlags.layoutDirty = true;
    }
  }, [selectedKey, lastSelectedKey]);

  useEffect(() => {
    if (data) {
      const removeInteractionObserver = initInteractionObserver(getViewport());
      initLayoutObserver();
      initCursorChangeObserver();
      return () => {
        removeInteractionObserver();
        removeLayoutObserver();
        removeCursorChangeObserver();
      };
    }
  }, [data]);
};
